import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { LiveKitRoom, VideoConference, RoomAudioRenderer } from '@livekit/components-react';
import '@livekit/components-styles';
import axios from 'axios';
import useServerUrl from '../hooks/useServerUrl';
import { useAppConfig } from '../config/runtime-config';
import livekitApiService from '../services/livekit-api.service';
import LocationForm from './LocationForm';
import './LocationVideoViewer.css';
import './LocationLiveKitViewer.css';

interface Location {
  id: string;
  name: string;
  description?: string;
  locationKey: string;
  userId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const LocationLiveKitViewer: React.FC = () => {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { theme } = useTheme();
  const SERVER_URL = useServerUrl();
  const config = useAppConfig(); // Get runtime config (includes livekitUrl)
  const livekitUrl = config?.livekitUrl || import.meta.env.VITE_LIVEKIT_URL || '';
  
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [livekitToken, setLivekitToken] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionState, setConnectionState] = useState<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');

  useEffect(() => {
    if (locationId) {
      fetchLocation();
    }
  }, [locationId, token]);

  useEffect(() => {
    if (location && location.isActive && livekitUrl && token && locationId) {
      fetchLiveKitToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, livekitUrl, token, locationId, user]);


  const fetchLocation = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${SERVER_URL}/api/locations/${locationId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLocation(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch location');
      console.error('Error fetching location:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLiveKitToken = async () => {
    if (!locationId || !token || !livekitUrl) {
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);

      const participantName = user?.email?.split('@')[0] || 'viewer';
      const livekitToken = await livekitApiService.generateToken(
        SERVER_URL,
        locationId,
        token,
        participantName,
        false, // canPublish - viewers don't publish
        true,  // hidden - viewers are hidden
      );

      setLivekitToken(livekitToken);
      setConnectionState('connecting');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to get LiveKit token';
      setError(errorMessage);
      setConnectionState('error');
      console.error('Error fetching LiveKit token:', err);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleEditLocation = () => {
    setShowLocationForm(true);
  };

  const handleLocationFormClose = () => {
    setShowLocationForm(false);
    if (locationId) {
      fetchLocation();
    }
  };

  const handleDisconnect = () => {
    setLivekitToken(null);
    setConnectionState('disconnected');
    setError(null);
  };

  if (loading || isConnecting) {
    return (
      <div className="location-video-viewer">
        <div className="loading">Loading location...</div>
      </div>
    );
  }

  if (error && !location) {
    return (
      <div className="location-video-viewer">
        <div className="error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleBackToHome} className="back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="location-video-viewer">
        <div className="error">
          <h2>Location Not Found</h2>
          <p>The requested location could not be found.</p>
          <button onClick={handleBackToHome} className="back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!livekitUrl) {
    return (
      <div className={`min-h-screen ${
        theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className={`${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          } border rounded-2xl p-4`}>
            <div className="error">
              <h2>LiveKit Not Configured</h2>
              <p>LiveKit server URL is not configured. Please contact your administrator.</p>
              <button onClick={handleBackToHome} className="back-btn">
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Location Header */}
        <div className={`${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        } border rounded-2xl p-4 mb-6`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={handleBackToHome}
                className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-slate-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Home
              </button>
              
              <div className="flex items-center space-x-3">
                <h1 className={`text-2xl font-bold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {location.name}
                </h1>
                
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                  location.isActive
                    ? theme === 'dark'
                      ? 'bg-green-600 text-white border border-green-500'
                      : 'bg-green-100 text-green-800 border border-green-300'
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 border border-gray-600'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                }`}>
                  {location.isActive ? '🟢 Active' : '🔴 Inactive'}
                </span>

                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                  connectionState === 'connected'
                    ? theme === 'dark'
                      ? 'bg-blue-600 text-white border border-blue-500'
                      : 'bg-blue-100 text-blue-800 border border-blue-300'
                    : connectionState === 'connecting'
                    ? theme === 'dark'
                      ? 'bg-yellow-600 text-white border border-yellow-500'
                      : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                    : connectionState === 'error'
                    ? theme === 'dark'
                      ? 'bg-red-600 text-white border border-red-500'
                      : 'bg-red-100 text-red-800 border border-red-300'
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 border border-gray-600'
                      : 'bg-gray-100 text-gray-600 border border-gray-300'
                }`}>
                  {connectionState === 'connected' && '🔵 Connected'}
                  {connectionState === 'connecting' && '🟡 Connecting'}
                  {connectionState === 'disconnected' && '⚪ Disconnected'}
                  {connectionState === 'error' && '🔴 Error'}
                </span>
              </div>
            </div>
            
            <button
              onClick={handleEditLocation}
              className={`p-2 rounded-lg transition-all duration-200 ${
                theme === 'dark'
                  ? 'text-gray-400 hover:text-white hover:bg-slate-700'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              title="Edit Location"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
          
          {location.description && (
            <p className={`text-sm mt-3 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              {location.description}
            </p>
          )}
        </div>

        {/* Error Banner */}
        {error && (
          <div className={`mb-4 p-4 rounded-lg ${
            theme === 'dark'
              ? 'bg-red-900/20 border-red-800 text-red-300'
              : 'bg-red-50 border-red-200 text-red-800'
          } border`}>
            <div className="flex items-center justify-between">
              <div>
                <strong>Error:</strong> {error}
              </div>
              <button
                onClick={fetchLiveKitToken}
                className={`ml-4 px-3 py-1 rounded text-sm ${
                  theme === 'dark'
                    ? 'bg-red-900/50 hover:bg-red-900/70'
                    : 'bg-red-100 hover:bg-red-200'
                }`}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* LiveKit Video Section */}
        {livekitToken && livekitUrl ? (
          <div className={`${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          } border rounded-2xl p-4`}>
            <div className="livekit-viewer-container" data-lk-theme="default">
              <LiveKitRoom
                serverUrl={livekitUrl}
                token={livekitToken}
                connect
                audio={false}
                video={false}
                options={{ publishDefaults: { videoSimulcastLayers: [] } }}
                onConnected={() => {
                  setConnectionState('connected');
                  setError(null);
                }}
                onDisconnected={() => {
                  setConnectionState('disconnected');
                  handleDisconnect();
                }}
                onError={(error: Error) => {
                  if (error.message?.includes('publish')) {
                    return;
                  }
                  setConnectionState('error');
                  if (error.message && !error.message.includes('pc connection')) {
                    setError(`Connection error: ${error.message}`);
                  }
                }}
                style={{ height: '100%', width: '100%' }}
              >
                <VideoConference />
                <RoomAudioRenderer />
              </LiveKitRoom>
            </div>
          </div>
        ) : livekitUrl ? (
          <div className={`${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          } border rounded-2xl p-8`}>
            <div className={`text-center py-12 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-lg font-semibold mb-2">Connecting to LiveKit room...</p>
              <p className="text-sm opacity-75">Please wait while we establish the connection.</p>
            </div>
          </div>
        ) : (
          <div className={`${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          } border rounded-2xl p-8`}>
            <div className={`text-center py-12 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              <div className="text-4xl mb-4">⚠️</div>
              <p className="text-lg font-semibold mb-2">LiveKit server URL is not configured.</p>
              <p className="text-sm opacity-75">Please contact your administrator.</p>
            </div>
          </div>
        )}
      </div>

      {/* Location Form Modal */}
      {showLocationForm && (
        <LocationForm
          location={location}
          onClose={handleLocationFormClose}
          onSuccess={handleLocationFormClose}
        />
      )}
    </div>
  );
};

export default LocationLiveKitViewer;
